import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import { useQuery, useAction, getLayoutByShareToken, importLayoutByShareToken } from "wasp/client/operations";
import { Card, CardContent } from "../client/components/ui/card";
import { Button } from "../client/components/ui/button";
import { Layers } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { useToast } from "../client/hooks/use-toast";
import DashboardLayout from "./widgets/DashboardLayout";
import { DashboardDataProvider, type DashboardData } from "./widgets/DashboardDataContext";
import type { LayoutSnapshot } from "./widgets/types";
import { PaymentPlanId } from "../payment/plans";

function emptyDashboardData(t: any, isFreePlan: boolean): DashboardData {
  return {
    trades: [],
    routines: [],
    goals: [],
    accounts: [],
    groups: [],
    strategies: [],
    sessions: [],
    metrics: {
      totalPnl: 0,
      totalTrades: 0,
      winRate: 0,
      winningTrades: 0,
      losingTrades: 0,
      profitFactor: 0,
      averageWin: 0,
      averageLoss: 0,
    },
    statsTrades: [],
    statsTradesList: [],
    equityCurveData: { points: [], totalPnl: 0, xMin: 0, xMax: 0 },
    accountEvents: [],
    fundedEvents: [],
    payoutEvents: [],
    violationsByDate: new Map(),
    pendingRoutines: [],
    topGoals: [],
    dateRange: { from: null, to: null },
    accountFilter: { type: "all" },
    setAccountFilter: () => {},
    strategyIds: [],
    setStrategyIds: () => {},
    statsPeriod: "30d" as const,
    statsStartDate: null,
    statsEndDate: null,
    setSelectedDay: () => {},
    handleCompleteRoutine: () => {},
    setSelectedMonth: () => {},
    isDark: false,
    themeId: "light",
    isFreePlan,
    readOnly: true,
    hasMentor: false,
    t,
    chartMode: "pnl",
    setChartMode: () => {},
    onAddTrade: () => {},
    onPromoteToFunded: () => {},
    onOpenPlanModal: () => {},
    charges: [],
    tptWalletContext: undefined,
    filteredAccounts: [],
    calendarEvents: [],
    pfmTemplateFilter: null,
    setPfmTemplateFilter: () => {},
    accountStatusFilter: null,
    setAccountStatusFilter: () => {},
    setDateRange: () => {},
    calendarMonth: new Date(),
    setCalendarMonth: () => {},
    showImportModal: false,
    setShowImportModal: () => {},
    showAddPayoutModal: false,
    setShowAddPayoutModal: () => {},
    isLoadingPfm: false,
    userId: undefined,
    hiddenAccountsCount: 0,
    showPlanModal: false,
    setShowPlanModal: () => {},
  };
}

export default function LayoutSharePreviewPage() {
  const t = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { data: user } = useAuth();
  const { data: preview, isLoading } = useQuery(getLayoutByShareToken, { shareToken: token ?? "" });
  const importFn = useAction(importLayoutByShareToken);

  const isFreePlan = !user || user.subscriptionPlan === PaymentPlanId.Free || !user.subscriptionPlan;

  if (isLoading) {
    return <div className="container mx-auto py-10 text-center text-muted-foreground">…</div>;
  }

  if (!preview) {
    return (
      <div className="container mx-auto py-10 max-w-xl">
        <Card>
          <CardContent className="p-8 text-center space-y-2">
            <h1 className="text-xl font-bold">{t.dashboard.layouts.previewLinkInactive}</h1>
            <p className="text-sm text-muted-foreground">{t.dashboard.layouts.previewLinkInactiveDesc}</p>
            <Button variant="default" className="mt-4" onClick={() => navigate("/dashboard")}>
              {t.dashboard.title}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const meta = t.dashboard.layouts.sharePreviewMeta
    .replace("{rows}", String((preview as any).rowCount))
    .replace("{widgets}", String((preview as any).widgetCount));
  // Sans pseudo présentable (le serveur n'expose jamais un email), pas de ligne
  // auteur, comme dans la bibliothèque communautaire.
  const authorUsername: string | null = (preview as any).authorUsername ?? null;
  const author = authorUsername
    ? t.dashboard.layouts.sharePreviewBy.replace("{username}", authorUsername)
    : null;

  async function handleImport() {
    if (!token) return;
    const result = await (importFn as any)({ shareToken: token });
    if (result) {
      navigate("/dashboard");
    } else {
      toast({ description: t.dashboard.widgetSaveError, variant: "destructive" });
    }
  }

  const rawSlots: any[] = (preview as any).slots ?? [];
  const previewLayout: LayoutSnapshot = {
    slots: rawSlots.map((s: any) => ({
      id: s.id,
      widgetKey: s.widgetKey,
      colStart: s.colStart,
      rowStart: s.rowStart,
      colSpan: s.colSpan,
      rowSpan: s.rowSpan,
    })),
  };

  const data = emptyDashboardData(t, isFreePlan);

  return (
    <div className="container mx-auto py-10">
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          {author && (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Layers className="h-3.5 w-3.5" />
              {author}
            </div>
          )}
          <h1 className="text-2xl font-bold">{(preview as any).name}</h1>
          <p className="text-sm text-muted-foreground">{meta}</p>
        </div>
        {user ? (
          <Button variant="default" onClick={handleImport}>
            {t.dashboard.layouts.importToLibrary}
          </Button>
        ) : (
          <div className="text-right">
            <p className="text-sm text-muted-foreground mb-2">{t.dashboard.layouts.previewLoginRequired}</p>
            <Button
              variant="default"
              onClick={() => navigate(`/login?redirectTo=/dashboard/layouts/share/${token}`)}
            >
              {t.dashboard.layouts.previewLoginCta}
            </Button>
          </div>
        )}
      </div>

      <DashboardDataProvider value={data}>
        <DashboardLayout
          layout={previewLayout}
          isEditMode={false}
          isMobile={false}
          onAddWidgetClick={() => {}}
          onRemoveWidget={() => {}}
          onWidgetSizeChange={() => {}}
          onMoveWidget={() => {}}
          onSwapWidgets={() => {}}
        />
      </DashboardDataProvider>
    </div>
  );
}
